import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/ui/components/ui/button";
import { Logo } from "@/shared/components/common/logo";
import { Pickaxe, BarChart3, MapPin, Bell, Settings, Activity } from "lucide-react";

type Props = {
  payload: Record<string, never>;
  onClose: () => void;
};

const FEATURES = [
  { icon: Pickaxe, labelKey: "onboarding.features.monitoring" },
  { icon: BarChart3, labelKey: "onboarding.features.analytics" },
  { icon: MapPin, labelKey: "onboarding.features.tracking" },
  { icon: Bell, labelKey: "onboarding.features.alerts" },
  { icon: Activity, labelKey: "onboarding.features.timeline" },
  { icon: Settings, labelKey: "onboarding.features.fleet" },
] as const;

const OnboardingModal = ({ onClose }: Props) => {
  const { t } = useTranslation("common");
  const [currentStep, setCurrentStep] = useState(1);

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex flex-row justify-center gap-2 mt-6">
      {[1, 2, 3].map((step) => (
        <div
          key={step}
          className={`h-2 rounded-full transition-all duration-300 ${
            step === currentStep ? "bg-primary w-6" : "bg-muted-foreground/40 w-2"
          }`}
        />
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="flex flex-col items-center py-8 px-6">
      <div className="mb-4">
        <Logo alt="MineMaestro" className="h-10 w-auto" />
      </div>
      <h2 className="text-2xl font-semibold text-center mb-4">
        {t("onboarding.step1.title")}
      </h2>
      <p className="text-center text-muted-foreground">
        {t("onboarding.step1.description")}
      </p>
      <ul className="mt-6 space-y-2 text-sm text-muted-foreground self-start w-full">
        <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />{t("onboarding.step1.bullet1")}</li>
        <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />{t("onboarding.step1.bullet2")}</li>
        <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />{t("onboarding.step1.bullet3")}</li>
      </ul>
    </div>
  );

  const renderStep2 = () => (
    <div className="flex flex-col py-4 px-6 gap-4">
      <h2 className="text-xl font-semibold text-center">
        {t("onboarding.step2.title")}
      </h2>
      <div className="grid grid-cols-2 gap-3">
        {FEATURES.map(({ icon: Icon, labelKey }) => (
          <div
            key={labelKey}
            className="flex items-center gap-3 rounded-lg border border-border bg-muted/50 px-4 py-3"
          >
            <Icon className="h-5 w-5 text-primary flex-shrink-0" />
            <span className="text-sm">{t(labelKey)}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="flex flex-col items-center py-8 px-6">
      <div className="mb-4">
        <Logo alt="MineMaestro" className="h-10 w-auto" />
      </div>
      <h2 className="text-2xl font-semibold text-center mb-4">
        {t("onboarding.step3.title")}
      </h2>
      <p className="text-center text-muted-foreground">
        {t("onboarding.step3.description")}
      </p>
      <ul className="mt-6 space-y-2 text-sm text-muted-foreground self-start w-full">
        <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />{t("onboarding.step3.bullet1")}</li>
        <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />{t("onboarding.step3.bullet2")}</li>
        <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />{t("onboarding.step3.bullet3")}</li>
      </ul>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return renderStep1();
    }
  };

  const isLastStep = currentStep === 3;

  return (
    <div className="flex flex-col">
      {renderCurrentStep()}
      <div className="flex flex-col px-6 pb-6">
        {renderStepIndicator()}
        <div className="flex justify-center mt-4">
          <Button
            onClick={isLastStep ? onClose : handleNext}
            className="w-full max-w-xs"
          >
            {isLastStep ? t("onboarding.actions.getStarted") : t("onboarding.actions.continue")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
